import type { State as ReduxSyncState } from 'src/reducers/syncing';
// Can be deleted once files that use these consts are .ts files
export const SYNCED = 'SYNCED';
export const SYNCING = 'SYNCING';
export const ERROR = 'ERROR';

export type SyncingState = 'SYNCED' | 'SYNCING' | 'ERROR';

export type SyncingLabel =
  | 'Saving'
  | 'Saving to profile'
  | 'Downloading'
  | 'Creating'
  | 'Uploading';

export type DoneLabel =
  | 'Saved'
  | 'Saved to profile'
  | 'Downloaded'
  | 'Added'
  | 'Removed'
  | 'Created'
  | 'Uploaded';

export type SyncingProps = Record<string, unknown> & {
  syncingLabel?: SyncingLabel;
  doneLabel?: DoneLabel;
};

export interface SyncStateItem {
  state: SyncingState;
  id?: number | string;
  action?: string;
  parent?: string;
  isRetryable?: boolean;
  props?: SyncingProps;
  singleton?: boolean;
}

export type SyncState = ReduxSyncState;

export interface StateForParent {
  state: SyncingState;
  props: SyncingProps;
  isRetryable: boolean;
}

export const stateForId = ({
  state,
  id,
  parent,
}: {
  state: { syncing: SyncState };
  id: number | string;
  parent: string;
}): SyncStateItem => {
  const parentState = state.syncing[parent] || [];
  return parentState.find((x) => x.id === id) || { state: 'SYNCED' };
};

export const itemStateForAction = ({
  state,
  action,
  id,
  parent,
}: {
  state: { syncing: SyncState };
  action: string;
  id: number | string;
  parent: string;
}) => {
  const parentState = state.syncing[parent] || [];
  return (
    parentState.find((x) => x.action === action && x.id === id) || {
      state: 'SYNCED',
    }
  );
};

export const stateForAction = ({
  state,
  action,
  parent,
}: {
  state: { syncing: SyncState };
  action: string;
  parent: string;
}): { state: SyncingState } => {
  const parentState = state.syncing[parent] || [];
  const isSyncing = parentState.find(
    (x) => x.action === action && x.state === 'SYNCING',
  );
  const isError = parentState.find(
    (x) => x.action === action && x.state === 'ERROR',
  );
  if (isSyncing) {
    return { state: 'SYNCING' };
  } else if (isError) {
    return { state: 'ERROR' };
  }
  return { state: 'SYNCED' };
};

export const itemsInError = ({
  state,
  parent,
}: {
  state: { syncing: SyncState };
  parent: string;
}) => {
  const parentState = state.syncing[parent] || [];
  return parentState.filter((x) => x.state === 'ERROR').map((item) => item.id);
};

const getSyncingStateForParent = (
  parent: SyncStateItem[] = [],
): SyncingState => {
  const state = parent.find((x) => x.state === 'ERROR') ? 'ERROR' : 'SYNCED';
  return parent.find((x) => x.state === 'SYNCING') ? 'SYNCING' : state;
};

const getRetryableStateForParent = (parent: SyncStateItem[] = []): boolean =>
  Boolean(parent.find((x) => typeof x.isRetryable !== 'undefined'));

const getSyncingPropsForParent = (
  parent: SyncStateItem[] = [],
): SyncingProps => {
  const syncStateItem = parent.length
    ? parent[parent.length - 1]
    : { props: {} };
  return typeof syncStateItem.props !== 'undefined' ? syncStateItem.props : {};
};

export const stateForParent = ({
  state,
  parent = '',
}: {
  state: {
    syncing: SyncState;
  };
  parent?: string;
}): StateForParent => {
  const parentState = state.syncing[parent];
  return {
    state: getSyncingStateForParent(parentState),
    props: getSyncingPropsForParent(parentState),
    isRetryable: getRetryableStateForParent(parentState),
  };
};
